import { Stack, Box, Tabs, Tab, Typography, IconButton, Tooltip, Skeleton } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router";
import { ContentItem, Publishing } from "../../../../../../../../shell/services/types";
import { VerticalSplitRounded, QueryStatsRounded, BarChartRounded, CodeRounded, ApiRounded, ManageAccountsRounded, ContentCopyRounded, WebRounded } from "@mui/icons-material";
import { ShuffleVariant } from "@zesty-io/material";
import { useSelector } from "react-redux";
import { AppState } from "../../../../../../../../shell/store/types";
import { ItemEditHeaderActions } from "./ItemEditHeaderActions";
import { VersionSelector } from "./VersionSelector";
import { LanguageSelector } from "./LanguageSelector";
import { ContentBreadcrumbs } from "../../../../components/ContentBreadcrumbs";
import { MoreMenu } from "./MoreMenu";
import { DuplicateItemDialog } from "./DuplicateItemDialog";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PreviewMenu } from "./PreviewMenu";
import { useGetInstalledAppsQuery } from "../../../../../../../../shell/services/accounts";
import { DuoModeSwitch } from "./DuoModeToggle";
import { useGetContentModelsQuery } from "../../../../../../../../shell/services/instance";
import { ContentItemWithDirtyAndPublishing } from "../../../../../../../../shell/services/types";
import { PublishStatus } from "./PublishStatus";
import RedirectsDialogContextProvider from "../../../../../../../seo/src/app/components/RedirectsDialogProvider";
const tabs = [{
  labelKey: "common.content",
  icon: VerticalSplitRounded,
  value: ""
}, {
  labelKey: "content.itemEditTabSeo",
  icon: QueryStatsRounded,
  value: "meta"
}, {
  labelKey: "content.itemEditTabRedirects",
  icon: ShuffleVariant,
  value: "redirects"
}, {
  labelKey: "content.itemEditTabAnalytics",
  icon: BarChartRounded,
  value: "analytics"
}, {
  labelKey: "content.itemEditTabHeadTags",
  icon: CodeRounded,
  value: "head"
}, {
  labelKey: "common.apis",
  icon: ApiRounded,
  value: "api"
}, {
  labelKey: "content.itemEditTabPublishStatus",
  icon: ManageAccountsRounded,
  value: "publishings"
}, {
  labelKey: "content.itemEditTabFreestyle",
  icon: WebRounded,
  value: "freestyle"
}
// {
//   label: "Studio",
//   icon: DesignServicesRounded,
//   value: "studio",
// },
];
type HeaderProps = {
  saving: boolean;
  onSave: () => void;
  hasError: boolean;
  isLoadingItem?: boolean;
};
export const ItemEditHeader = ({
  saving,
  onSave,
  hasError,
  isLoadingItem
}: HeaderProps) => {
  const {
    t
  } = useTranslation();
  const {
    modelZUID,
    itemZUID
  } = useParams<{
    modelZUID: string;
    itemZUID: string;
  }>();
  const location = useLocation();
  const history = useHistory();
  const [showDuplicateItemDialog, setShowDuplicateItemDialog] = useState(false);
  const {
    data: installedApps
  } = useGetInstalledAppsQuery();
  const {
    data: contentModels
  } = useGetContentModelsQuery(null, {
    refetchOnMountOrArgChange: true
  });
  const item = useSelector((state: AppState) => state.content[itemZUID] as ContentItemWithDirtyAndPublishing);
  const modelLabel = contentModels?.find(model => model.ZUID === modelZUID)?.label;
  const type = contentModels?.find(model => model.ZUID === modelZUID)?.type ?? "";
  const layoutsAppInstalled = installedApps?.find(app => app.appZUID === "80-d8abaff6ef-wxs830");
  const headerTitle = item?.web?.metaTitle || item?.web?.metaLinkText || "";
  const resolveStudioPath = () => {
    if (item?.web?.path) return item.web.path;
    if (item?.web?.pathPart === "zesty_home") return "/";
    if (item?.web?.pathPart) return item.web.pathPart.startsWith("/") ? item.web.pathPart : `/${item.web.pathPart}`;
    return "/";
  };
  return <>
      <Box px={4} pt={4} pb={type === "block" ? 1 : 0} sx={{
      backgroundColor: "background.paper",
      color: "text.primary",
      borderBottom: theme => `2px solid ${theme?.palette?.border} `,
      "*": {
        boxSizing: "border-box"
      },
      containerType: "inline-size"
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Box display="flex" justifyContent="space-between" gap={4} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <ContentBreadcrumbs data-sentry-element="ContentBreadcrumbs" data-sentry-source-file="index.tsx" />
            {isLoadingItem && (!modelLabel || !item || !Object.keys(item?.web).length) ? <Stack>
                <Typography variant="h3" sx={{
              width: 667
            }}>
                  <Skeleton variant="text" />
                </Typography>
                <Typography variant="h3" sx={{
              width: 442
            }}>
                  <Skeleton variant="text" />
                </Typography>
              </Stack> : <Typography variant="h3" fontWeight="700" sx={{
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
            wordWrap: "break-word",
            hyphens: "auto",
            overflow: "hidden"
          }}>
                {(type === "block" ? `${modelLabel}: ${headerTitle}` : headerTitle) || ""}
              </Typography>}
          </Box>
          <Stack gap={1.25} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
            <Box display="flex" gap={1} alignItems="center" sx={{
            "@container (max-width: 900px)": {
              flexWrap: "wrap"
            },
            minHeight: 32
          }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
              {isLoadingItem && (!item || !Object.keys(item.web).length || !Object.keys(item.meta).length) ? <Stack direction="row" gap={3} mr={1}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="circular" width={16} height={16} />
                </Stack> : <>
                  <MoreMenu />
                  <Tooltip title={t("content.itemEditDuplicateItem")} enterDelay={1000} enterNextDelay={1000} placement="bottom-start">
                    <IconButton size="small" onClick={() => setShowDuplicateItemDialog(true)}>
                      <ContentCopyRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {type !== "dataset" && type !== "block" && <PreviewMenu />}
                </>}
              {type === "block" && <>
                  <LanguageSelector />
                  <VersionSelector activeVersion={item?.meta?.version} />
                </>}
              <RedirectsDialogContextProvider data-sentry-element="RedirectsDialogContextProvider" data-sentry-source-file="index.tsx">
                <ItemEditHeaderActions saving={saving} onSave={onSave} hasError={hasError} isLoadingItem={isLoadingItem} data-sentry-element="ItemEditHeaderActions" data-sentry-source-file="index.tsx" />
              </RedirectsDialogContextProvider>
            </Box>
            <PublishStatus currentVersion={item?.web?.version} data-sentry-element="PublishStatus" data-sentry-source-file="index.tsx" />
          </Stack>
        </Box>
        {!!type && type !== "block" && <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Tabs variant="scrollable" scrollButtons={false} value={tabs.find(tab => tab.value !== "" && location.pathname.includes(tab.value))?.value || ""} onChange={(event, value) => {
          if (value === "studio") {
            history.push(`/studio?path=${resolveStudioPath()}`);
            return;
          }
          history.push(value ? `/content/${modelZUID}/${itemZUID}/${value}` : `/content/${modelZUID}/${itemZUID}`);
        }} sx={{
          position: "relative",
          top: "2px"
        }}>
              {tabs.map(tab => {
            if (tab.value === "freestyle" && !layoutsAppInstalled) {
              return;
            } else return <Tab key={tab.value} disableRipple label={t(tab.labelKey)} value={tab.value} icon={<tab.icon fontSize="small" />} iconPosition="start" />;
          })}
            </Tabs>
            <Box display="flex" gap={2} alignItems="center">
              <DuoModeSwitch />
              <LanguageSelector />
              <VersionSelector activeVersion={item?.meta?.version} />
            </Box>
          </Box>}
      </Box>
      {showDuplicateItemDialog && <DuplicateItemDialog onClose={() => setShowDuplicateItemDialog(false)} />}
    </>;
};